import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useRef } from 'react';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import FormikButton from '../../../common/formik/FormikButton';
import FormikGrid from '../../../common/formik/FormikGrid';
import FormikTextField from '../../../common/formik/FormikTextField';
import { useCreateRole, useUpdateRole } from '../Role.hooks';
import { Role, RoleCreateDto } from '../Role.interface';

export interface RoleSaveDialogProps {
  open: boolean;
  role?: Role;
  onCancel?: () => void;
  onSave?: (role: Role, created?: boolean) => void;
}

const RoleSaveDialog: React.FC<RoleSaveDialogProps> = props => {
  const { open, role, onSave, onCancel } = props;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const formikRef = useRef<FormikProps<RoleCreateDto> | null>(null);
  const {
    role: createdRole,
    isLoading: isCreating,
    error: createError,
    createRole
  } = useCreateRole();
  const {
    role: updatedRole,
    isLoading: isUpdating,
    error: updateError,
    updateRole
  } = useUpdateRole(role?.id || 0);
  const isLoading = isCreating || isUpdating;
  const error = createError || updateError;

  useEffect(() => {
    formikRef.current?.setSubmitting(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (createdRole) {
      onSaveRef.current?.(createdRole, true);
    }
  }, [createdRole]);
  useEffect(() => {
    if (updatedRole) {
      onSaveRef.current?.(updatedRole);
    }
  }, [updatedRole]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <Formik<RoleCreateDto>
        innerRef={formikRef}
        initialValues={{ name: role?.name || '' }}
        validationSchema={Yup.object({
          name: Yup.string().required('Required')
        })}
        onSubmit={dto => {
          role ? updateRole(dto) : createRole(dto);
        }}
      >
        <Form autoComplete='off'>
          <DialogTitle>{role ? 'Update' : 'Create New'} Role</DialogTitle>
          <DialogContent>
            <FormikGrid>
              <FormikTextField
                autoFocus
                type='text'
                name='name'
                label='Role Name'
              />
            </FormikGrid>
            {error && <Alert severity='error'>{error.message}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={onCancel}>Cancel</Button>
            <FormikButton>{role ? 'Update' : 'Create'}</FormikButton>
          </DialogActions>
        </Form>
      </Formik>
    </Dialog>
  );
};

export default RoleSaveDialog;
